/**
 * @module public/js/pages/loginController
 * @summary Provides a dependency-injected login controller for form handling.
 */

import { consumePendingLoginIssue } from '../core/auth/pendingLoginIssue.js';

const GOOGLE_PROVIDER_ID = 'google.com';
const MICROSOFT_PROVIDER_ID = 'microsoft.com';

/**
 * Resolve which pre-login federated auth buttons should be available.
 *
 * @param {Record<string, unknown>} [authConfig={}] Tenant auth config.
 * @returns {{ googleEnabled: boolean, microsoftEnabled: boolean }} Provider controls.
 */
export function resolveLoginAuthProviderControls(authConfig = {}) {
  const rawProviders = Array.isArray(authConfig.providers)
    ? authConfig.providers
    : Array.isArray(authConfig.enterpriseProviders)
      ? authConfig.enterpriseProviders
      : [];
  const providerIds = new Set(
    rawProviders.map((provider) =>
      String(provider?.providerId || '')
        .trim()
        .toLowerCase()
    )
  );
  const googleExplicitlyDisabled =
    authConfig.googleEnabled === false ||
    authConfig.googleSignInEnabled === false;
  const microsoftExplicitlyDisabled =
    authConfig.microsoftEnabled === false ||
    authConfig.microsoftSignInEnabled === false;

  return {
    googleEnabled:
      !googleExplicitlyDisabled &&
      (authConfig.googleEnabled === true ||
        authConfig.googleSignInEnabled === true ||
        providerIds.has(GOOGLE_PROVIDER_ID)),
    microsoftEnabled:
      !microsoftExplicitlyDisabled &&
      (authConfig.microsoftEnabled === true ||
        authConfig.microsoftSignInEnabled === true ||
        providerIds.has(MICROSOFT_PROVIDER_ID)),
  };
}

/**
 * Creates a login controller with injected dependencies for easier testing.
 *
 * @param {Object} options configuration for the controller.
 * @param {{
 *   signInUser: (email: string, password: string) => Promise<{ user?: { uid?: string } }>,
 *   signInWithGoogle?: () => Promise<{ user?: { uid?: string } }>,
 *   signInWithMicrosoft?: () => Promise<{ user?: { uid?: string } }>,
 *   storeConnectionStatus: (userId: string) => Promise<void>,
 * }} options.loginService
 * @param {Promise<unknown>} options.authReady promise that resolves when auth is initialised.
 * @param {{
 *   startAttempt?: (context?: Record<string, unknown>) => void,
 *   endAttempt?: (result: { result?: unknown, error?: unknown }) => void,
 *   reset?: () => void,
 * }} options.authTelemetry
 * @param {{
 *   start?: () => void,
 *   stop?: () => void,
 *   getStatus?: () => string | null,
 *   onStatusChange?: (listener: (status: string) => void) => () => void,
 * }} options.netHealthService
 * @param {Document} [options.document] DOM document (default global document).
 * @param {Window} [options.window] DOM window (default global window).
 * @param {Console} [options.logger] logging target (default console).
 * @returns {{ handleSubmit: (event: { preventDefault: () => void }) => Promise<void>, handleGoogleSignIn: (event?: { preventDefault?: () => void }) => Promise<void>, handleMicrosoftSignIn: (event?: { preventDefault?: () => void }) => Promise<void>, applyAuthProviderConfig: (authConfig?: Record<string, unknown>) => { googleEnabled: boolean, microsoftEnabled: boolean }, _internals: Record<string, unknown> }}
 *          controller API with exposed internals for testing.
 */
export function createLoginController({
  loginService,
  authReady,
  authTelemetry,
  netHealthService,
  document: providedDocument,
  window: providedWindow,
  logger = console,
}) {
  const doc =
    providedDocument ?? (typeof document !== 'undefined' ? document : null);
  const win = providedWindow ?? (typeof window !== 'undefined' ? window : null);

  let unsubscribeNetHealth = null;
  let isLoginInFlight = false;
  let queuedAttempt = null;
  let retryTimeoutId = null;
  let countdownIntervalId = null;

  function clearCountdownTimers() {
    if (!win) {
      retryTimeoutId = null;
      countdownIntervalId = null;
      return;
    }

    if (retryTimeoutId) {
      win.clearTimeout?.(retryTimeoutId);
      retryTimeoutId = null;
    }

    if (countdownIntervalId) {
      win.clearInterval?.(countdownIntervalId);
      countdownIntervalId = null;
    }

    if (win.__loginCountdownTimerId) {
      win.clearInterval?.(win.__loginCountdownTimerId);
      delete win.__loginCountdownTimerId;
    }
  }

  function getResponseNode() {
    if (!doc || typeof doc.getElementById !== 'function') {
      return null;
    }

    const node = doc.getElementById('responseData');
    if (!node || typeof node !== 'object') {
      return null;
    }

    return 'innerHTML' in node ? node : null;
  }

  function renderResponse(html) {
    const node = getResponseNode();
    if (node) {
      node.innerHTML = html;
    }
  }

  /**
   * Show or hide federated sign-in buttons from tenant auth settings.
   *
   * @param {Record<string, unknown>} [authConfig={}] Tenant auth config.
   * @returns {{ googleEnabled: boolean, microsoftEnabled: boolean }} Controls applied.
   */
  function applyAuthProviderConfig(authConfig = {}) {
    const controls = resolveLoginAuthProviderControls(authConfig);
    const googleButton = doc?.getElementById?.('googleSignInButton');
    const microsoftButton = doc?.getElementById?.('microsoftSignInButton');
    const options = doc?.getElementById?.('federatedSignInOptions');

    if (googleButton && 'hidden' in googleButton) {
      googleButton.hidden = !controls.googleEnabled;
    }
    if (microsoftButton && 'hidden' in microsoftButton) {
      microsoftButton.hidden = !controls.microsoftEnabled;
    }
    if (options && 'hidden' in options) {
      options.hidden = !controls.googleEnabled && !controls.microsoftEnabled;
    }

    return controls;
  }

  /**
   * Render any one-shot auth issue left behind by a prior redirect.
   *
   * @returns {boolean} True when a pending issue was rendered.
   */
  function renderPendingLoginIssue() {
    const storage = win?.sessionStorage;
    const message = consumePendingLoginIssue(storage);
    if (!message) {
      return false;
    }

    renderResponse(`
      <div role="alert" aria-live="assertive" style="color: red;">
        <p>${message}</p>
      </div>
    `);
    return true;
  }

  function formatCountdownLabel(retryAt) {
    const now = Date.now();
    const msRemaining = Math.max(0, retryAt - now);
    const secondsRemaining = Math.ceil(msRemaining / 1000);
    if (!Number.isFinite(secondsRemaining) || secondsRemaining <= 0) {
      return 'a moment';
    }

    if (secondsRemaining >= 60) {
      const minutes = Math.ceil(secondsRemaining / 60);
      return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }

    return `${secondsRemaining} second${secondsRemaining === 1 ? '' : 's'}`;
  }

  function startCountdown(retryAt, onElapsed) {
    clearCountdownTimers();
    const update = () => {
      const label = formatCountdownLabel(retryAt);
      renderResponse(`
        <div role="status" aria-live="polite" class="login-status login-status--countdown">
          <p>Please wait ${label} before trying again.</p>
        </div>
      `);
    };

    update();
    if (win) {
      countdownIntervalId = win.setInterval?.(update, 1_000) ?? null;
      if (countdownIntervalId) {
        win.__loginCountdownTimerId = countdownIntervalId;
      }

      const delay = Math.max(0, retryAt - Date.now());
      if (typeof onElapsed === 'function') {
        retryTimeoutId = win.setTimeout?.(onElapsed, delay) ?? null;
      }
    }
  }

  function teardownAfterAttempt() {
    clearCountdownTimers();
    queuedAttempt = null;

    if (typeof unsubscribeNetHealth === 'function') {
      try {
        unsubscribeNetHealth();
      } catch (err) {
        logger?.warn?.('[login] failed to unsubscribe NetHealth listener', err);
      }
      unsubscribeNetHealth = null;
    }

    try {
      netHealthService?.stop?.();
    } catch (err) {
      logger?.warn?.('[login] failed to stop NetHealthService', err);
    }

    try {
      authTelemetry?.reset?.();
    } catch (err) {
      logger?.warn?.('[login] failed to reset AuthTelemetry', err);
    }
  }

  function createAttemptState(email, password, queued = false) {
    return { email, password, queued };
  }

  function handleNetHealthStatus(status) {
    if (!queuedAttempt) {
      return undefined;
    }

    if (status !== 'OFFLINE') {
      const attempt = queuedAttempt;
      queuedAttempt = null;
      return attemptLogin(attempt.email, attempt.password, true);
    }

    return undefined;
  }

  function ensureNetHealth() {
    try {
      netHealthService?.start?.();
    } catch (err) {
      logger?.warn?.('[login] failed to start NetHealthService', err);
    }

    if (
      !unsubscribeNetHealth &&
      typeof netHealthService?.onStatusChange === 'function'
    ) {
      unsubscribeNetHealth = netHealthService.onStatusChange(
        handleNetHealthStatus
      );
    }
  }

  function handleOfflineRetry(email, password) {
    queuedAttempt = createAttemptState(email, password, true);
    renderResponse(`
      <div role="alert" aria-live="assertive" class="login-status login-status--offline">
        <p>We couldn't reach the login service. We'll retry once you're back online.</p>
      </div>
    `);
  }

  function getRetryAt(error) {
    if (!error || typeof error !== 'object') {
      return null;
    }

    const retryAt = Number(error.retryAt ?? 0);
    if (Number.isFinite(retryAt) && retryAt > 0) {
      return retryAt;
    }

    const openUntil = Number(error.openUntil ?? 0);
    if (Number.isFinite(openUntil) && openUntil > 0) {
      return openUntil;
    }

    return null;
  }

  function scheduleResilienceRetry(email, password, retryAt) {
    queuedAttempt = createAttemptState(email, password, true);
    startCountdown(retryAt, () => {
      const attempt = queuedAttempt;
      queuedAttempt = null;
      if (attempt) {
        void attemptLogin(attempt.email, attempt.password, true);
      }
    });
  }

  /**
   * Finish a successful Firebase login by caching connection state and routing home.
   *
   * @param {{ user?: { uid?: string } }} userCredential - Firebase user credential.
   * @returns {Promise<void>}
   */
  async function completeSuccessfulLogin(userCredential) {
    const user = userCredential?.user;

    try {
      await loginService.storeConnectionStatus(user?.uid ?? '');
    } catch (statusError) {
      logger?.warn?.(
        'Continuing to home without cached CRM connection info:',
        statusError
      );
      renderResponse(`
        <div role="status" aria-live="polite" style="color: #8a6d3b;">
          <p>Signed in, but couldn't refresh your CRM connection status just yet.</p>
        </div>
      `);
    }

    teardownAfterAttempt();
    if (win && win.location) {
      win.location.href = 'home.html';
    }
  }

  async function attemptLogin(email, password, fromQueue = false) {
    if (isLoginInFlight) {
      return;
    }

    isLoginInFlight = true;
    ensureNetHealth();

    try {
      await authReady;
    } catch (readyError) {
      logger?.warn?.('[login] authReady failure (continuing)', readyError);
    }

    try {
      authTelemetry?.startAttempt?.({
        flow: 'PASSWORD',
        entrypoint: fromQueue ? 'login.autoRetry' : 'login.formSubmit',
      });
    } catch (err) {
      logger?.warn?.('[login] failed to start auth telemetry', err);
    }

    try {
      const userCredential = await loginService.signInUser(email, password);

      try {
        authTelemetry?.endAttempt?.({ result: 'SUCCESS', error: null });
      } catch (err) {
        logger?.warn?.(
          '[login] failed to finish auth telemetry (success)',
          err
        );
      }

      await completeSuccessfulLogin(userCredential);
    } catch (error) {
      try {
        authTelemetry?.endAttempt?.({ result: 'ERROR', error });
      } catch (err) {
        logger?.warn?.('[login] failed to finish auth telemetry (error)', err);
      }

      logger?.error?.('[LoginForm] Login failed', {
        code: error?.code,
        message: error?.message,
        raw: error,
      });

      handleLoginError(error, email, password);
    } finally {
      isLoginInFlight = false;

      if (!queuedAttempt) {
        try {
          netHealthService?.stop?.();
        } catch (err) {
          logger?.warn?.('[login] failed to stop NetHealthService', err);
        }
      }
    }
  }

  /**
   * Render a federated sign-in specific error message.
   *
   * @param {unknown} error - Firebase sign-in error.
   * @param {string} providerName - Human-friendly provider name.
   * @returns {void}
   */
  function handleFederatedLoginError(error, providerName) {
    clearCountdownTimers();

    const code = (error && typeof error === 'object' && error.code) || '';

    switch (code) {
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        renderResponse(`
          <div role="status" aria-live="polite" class="login-status login-status--cancelled">
            <p>${providerName} sign-in was cancelled. Nothing changed.</p>
          </div>
        `);
        break;
      case 'auth/account-exists-with-different-credential':
        renderResponse(`
          <div role="alert" aria-live="assertive" style="color: red;">
            <p>An account already exists with that email. Sign in with your existing method first, then connect ${providerName} from your profile.</p>
          </div>
        `);
        break;
      case 'auth/operation-not-allowed':
        renderResponse(`
          <div role="alert" aria-live="assertive" style="color: red;">
            <p>${providerName} Sign-In is not enabled for this Firebase project. Ask an admin to enable it, then try again.</p>
          </div>
        `);
        break;
      case 'auth/user-disabled':
        renderResponse(`
          <div role="alert" aria-live="assertive" style="color: red;">
            <p>Your account has been disabled. Please reach out to your account manager.</p>
          </div>
        `);
        break;
      case 'auth/network-request-failed':
        renderResponse(`
          <div role="alert" aria-live="assertive" class="login-status login-status--offline">
            <p>We couldn't reach ${providerName} Sign-In. Check your connection and try again.</p>
          </div>
        `);
        break;
      default:
        renderResponse(`
          <div role="alert" aria-live="assertive" style="color: red;">
            <p>Something went wrong while signing in with ${providerName}. Please try again, or use email and password.</p>
          </div>
        `);
    }
  }

  /**
   * Render a Google Sign-In specific error message.
   *
   * @param {unknown} error - Firebase sign-in error.
   * @returns {void}
   */
  function handleGoogleLoginError(error) {
    handleFederatedLoginError(error, 'Google');
  }

  /**
   * Render a Microsoft Sign-In specific error message.
   *
   * @param {unknown} error - Firebase sign-in error.
   * @returns {void}
   */
  function handleMicrosoftLoginError(error) {
    handleFederatedLoginError(error, 'Microsoft');
  }

  /**
   * Attempt a Firebase popup sign-in with a configured federated provider.
   *
   * @param {{ providerName: string, flow: string, entrypoint: string, serviceMethodName: string, unavailableCode: string, handleError: (error: unknown) => void }} options - Provider sign-in options.
   * @returns {Promise<void>}
   */
  async function attemptFederatedLogin({
    providerName,
    flow,
    entrypoint,
    serviceMethodName,
    unavailableCode,
    handleError,
  }) {
    if (isLoginInFlight) {
      return;
    }

    isLoginInFlight = true;
    queuedAttempt = null;
    ensureNetHealth();

    try {
      await authReady;
    } catch (readyError) {
      logger?.warn?.('[login] authReady failure (continuing)', readyError);
    }

    try {
      authTelemetry?.startAttempt?.({
        flow,
        entrypoint,
      });
    } catch (err) {
      logger?.warn?.('[login] failed to start auth telemetry', err);
    }

    try {
      if (typeof loginService[serviceMethodName] !== 'function') {
        throw Object.assign(
          new Error(`${providerName} sign-in is unavailable.`),
          {
            code: unavailableCode,
          }
        );
      }

      const userCredential = await loginService[serviceMethodName]();

      try {
        authTelemetry?.endAttempt?.({ result: 'SUCCESS', error: null });
      } catch (err) {
        logger?.warn?.(
          '[login] failed to finish auth telemetry (success)',
          err
        );
      }

      await completeSuccessfulLogin(userCredential);
    } catch (error) {
      try {
        authTelemetry?.endAttempt?.({ result: 'ERROR', error });
      } catch (err) {
        logger?.warn?.('[login] failed to finish auth telemetry (error)', err);
      }

      logger?.error?.(`[LoginForm] ${providerName} sign-in failed`, {
        code: error?.code,
        message: error?.message,
        raw: error,
      });

      handleError(error);
    } finally {
      isLoginInFlight = false;

      if (!queuedAttempt) {
        try {
          netHealthService?.stop?.();
        } catch (err) {
          logger?.warn?.('[login] failed to stop NetHealthService', err);
        }
      }
    }
  }

  /**
   * Attempt Firebase Google popup sign-in.
   *
   * @returns {Promise<void>}
   */
  async function attemptGoogleLogin() {
    return attemptFederatedLogin({
      providerName: 'Google',
      flow: 'GOOGLE',
      entrypoint: 'login.googleButton',
      serviceMethodName: 'signInWithGoogle',
      unavailableCode: 'topsail/google-sign-in-unavailable',
      handleError: handleGoogleLoginError,
    });
  }

  /**
   * Attempt Firebase Microsoft popup sign-in.
   *
   * @returns {Promise<void>}
   */
  async function attemptMicrosoftLogin() {
    return attemptFederatedLogin({
      providerName: 'Microsoft',
      flow: 'MICROSOFT',
      entrypoint: 'login.microsoftButton',
      serviceMethodName: 'signInWithMicrosoft',
      unavailableCode: 'topsail/microsoft-sign-in-unavailable',
      handleError: handleMicrosoftLoginError,
    });
  }

  function handleLoginError(error, email, password) {
    clearCountdownTimers();

    const code = (error && typeof error === 'object' && error.code) || '';

    switch (code) {
      case 'auth/user-not-found':
        renderResponse(`
          <div role="alert" aria-live="assertive" style="color: red;">
            <p>We couldn’t find an account with that email. Double-check the spelling or sign up if you’re new.</p>
          </div>
        `);
        break;
      case 'auth/wrong-password':
      case 'auth/invalid-login-credentials':
        renderResponse(`
          <div role="alert" aria-live="assertive" style="color: red;">
            <p>The email or password you entered is incorrect. Try again, or use Forgot Password to reset it.</p>
          </div>
        `);
        break;
      case 'auth/too-many-requests':
        renderResponse(`
          <div role="alert" aria-live="assertive" style="color: red;">
            <p>Your account has been temporarily locked due to too many failed login attempts. Please wait a few minutes before trying again.</p>
          </div>
        `);
        break;
      case 'auth/invalid-email':
        renderResponse(`
          <div role="alert" aria-live="assertive" style="color: red;">
            <p>That doesn’t look like a valid email address. Fix any typos or remove extra spaces, then try again.</p>
          </div>
        `);
        break;
      case 'auth/user-disabled':
        renderResponse(`
          <div role="alert" aria-live="assertive" style="color: red;">
            <p>Your account has been disabled. Please reach out to your account manager.</p>
          </div>
        `);
        break;
      case 'auth/network-request-failed':
        handleOfflineRetry(email, password);
        break;
      case 'auth/resilience-timeout': {
        const retryAt = getRetryAt(error);
        if (retryAt) {
          scheduleResilienceRetry(email, password, retryAt);
        } else {
          renderResponse(`
            <div role="alert" aria-live="assertive" class="login-status login-status--timeout">
              <p>The login service is taking longer than expected. Please try again in a few moments.</p>
            </div>
          `);
        }
        break;
      }
      case 'auth-resilience/circuit-open':
      case 'auth/resilience-circuit-open': {
        const retryAt = getRetryAt(error);
        if (retryAt) {
          startCountdown(retryAt);
        } else {
          renderResponse(`
            <div role="alert" aria-live="assertive" class="login-status login-status--breaker">
              <p>We paused login after repeated network issues. Please wait a few moments and try again.</p>
            </div>
          `);
        }
        break;
      }
      default:
        renderResponse(`
          <div role="alert" aria-live="assertive" style="color: red;">
            <p>Something went wrong while signing you in. Please try again, and if the issue continues, contact austin@topsail.app for help.</p>
          </div>
        `);
    }
  }

  async function handleSubmit(event) {
    event?.preventDefault?.();
    clearCountdownTimers();
    queuedAttempt = null;

    const emailInput = doc?.getElementById?.('email');
    const passwordInput = doc?.getElementById?.('password');
    const email = emailInput && 'value' in emailInput ? emailInput.value : '';
    const password =
      passwordInput && 'value' in passwordInput ? passwordInput.value : '';

    await attemptLogin(email, password);
  }

  /**
   * Handle the login page Google Sign-In button.
   *
   * @param {{ preventDefault?: () => void }} [event] - Click event.
   * @returns {Promise<void>}
   */
  async function handleGoogleSignIn(event) {
    event?.preventDefault?.();
    clearCountdownTimers();
    queuedAttempt = null;

    await attemptGoogleLogin();
  }

  /**
   * Handle the login page Microsoft Sign-In button.
   *
   * @param {{ preventDefault?: () => void }} [event] - Click event.
   * @returns {Promise<void>}
   */
  async function handleMicrosoftSignIn(event) {
    event?.preventDefault?.();
    clearCountdownTimers();
    queuedAttempt = null;

    await attemptMicrosoftLogin();
  }

  return {
    handleSubmit,
    handleGoogleSignIn,
    handleMicrosoftSignIn,
    applyAuthProviderConfig,
    renderPendingLoginIssue,
    _internals: {
      attemptLogin,
      attemptGoogleLogin,
      attemptMicrosoftLogin,
      attemptFederatedLogin,
      handleLoginError,
      handleGoogleLoginError,
      handleMicrosoftLoginError,
      handleFederatedLoginError,
      applyAuthProviderConfig,
      clearCountdownTimers,
      completeSuccessfulLogin,
      teardownAfterAttempt,
      getResponseNode,
      renderPendingLoginIssue,
      formatCountdownLabel,
      ensureNetHealth,
      handleOfflineRetry,
      startCountdown,
      getRetryAt,
      scheduleResilienceRetry,
      state: () => ({
        unsubscribeNetHealth,
        isLoginInFlight,
        queuedAttempt,
        retryTimeoutId,
        countdownIntervalId,
      }),
    },
  };
}

export default createLoginController;
